import { Injectable, inject } from '@angular/core';
import { isEqual } from 'lodash-es';
import * as utilEnum from 'src/app/services/util.enum';
import { UtilService } from 'src/app/services/util.service';
import { lookupTypes } from 'src/app/components/pg-lookup-configuration/pg-lookup-configuration.component';
import { GlobalConstants } from 'src/app/base/global.constants';

/**
 * Interface for the context required by reference operations
 */
export interface ReferenceContext {
  referencesTasks: { addedReference: any[]; deletedReference: any[] };
  currentSelectedItem: any;
  appId: string;
  allRoles?: any[];
  foreignKeys?: any[];
  allSelectedScreens?: any;
  screenLookupDefinitionDetails?: any[];
  pageSection?: { createdPageSectionMap: any; updatedPageSectionMap: any; deletedPageSections: any[] };
  updatedObject?: any;
  gridChildrenFlds?: any[];
}

/**
 * Input data for processing property changes
 */
export interface PropertyChangeInput {
  data: any;
  props: any;
  olddata: any;
  node: any;
  nType: string;
  adInfo: any;
  queryViewConfigurationId?: string;
  stdGridConfigurationId?: string;
  hasPropertyChanged: (olddata: any, props: any, prop: string) => boolean;
  tree?: any;
}

/**
 * Result of processing property changes
 */
export interface PropertyChangeResult {
  props: any;
  resetPrps: boolean;
  screenLookupDefinitionDetails?: any[];
}

/**
 * Service to handle reference-related operations for screens
 * Extracted from ScreensComponent to reduce complexity
 */
@Injectable({
  providedIn: 'root'
})
export class ScreensReferenceService {
  private util = inject(UtilService);

  private static readonly ignorablePathTypes = new Set([
    'page',
    'homepage',
    'pageview',
    'breadcrumb',
    'navigationtabs',
    'buttongroup',
    'tablesearch',
    'quickfilter',
    'filtersandsearch',
    'app_navigation',
    'app_menu_header',
    'app_logo',
    'app_name',
    'user_info',
    'language',
    'logout'
  ]);

  /** Terminal page types where path traversal stops (outermost page containers) */
  private static readonly terminalPathTypes = new Set(['app_navigation', 'homepage', 'pageview']);

  constructor() { }

  /** Builds the path string by traversing up the node hierarchy */
  formPath(node: any, path: string): string {
    const type = node?.type?.toLowerCase();
    const nodeId = node?.data?.id;

    if (!type) {
      return path;
    }

    if (nodeId === 'APP_NAVIGATION' && type === 'appheader') {
      return path;
    }

    if (ScreensReferenceService.terminalPathTypes.has(type)) {
      return path;
    }

    if (type === 'appheader') {
      path += `${nodeId}`;
      return path;
    }

    if (!ScreensReferenceService.ignorablePathTypes.has(type)) {
      path += `${nodeId}/`;
    }

    return node?.parent ? this.formPath(node.parent, path) : path;
  }

  /**
   * Gets the additional path for a node
   */
  getAdditionalPath(node: any): string {
    let path = this.formPath(node, '');
    if (path.includes('/')) {
      path = path.split('/').reverse().join('/');
    }
    if (path.startsWith('/')) {
      path = path.substring(1);
    }
    return path;
  }

  /**
   * Gets the property name based on the type
   */
  getPropName(type: string): string {
    let propName;
    switch (type) {
      case 'form':
      case 'formsection':
      case 'tab':
      case 'tabview':
      case 'formfield':
      case 'customComponent':
      case 'childlist':
        propName = utilEnum.propertyName.FORM;
        break;
      case 'queryview':
        propName = utilEnum.propertyName.QUERY_VIEW;
        break;
      case 'standarddatagrid':
        propName = utilEnum.propertyName.STANDARD_DATAGRID;
        break;
      case 'grid':
      case 'gridcolumn':
        propName = utilEnum.propertyName.TABLE;
        break;
      case 'tablesearch':
      case 'searchfield':
        propName = utilEnum.propertyName.TABLE_SEARCH;
        break;
      case 'quickfilter':
      case 'filterfield':
        propName = utilEnum.propertyName.QUICK_FILTERS;
        break;
      case 'captionbar':
      case 'captionitem':
        propName = utilEnum.propertyName.CAPTION_BAR;
        break;
      case 'page':
      case 'pageName':
        propName = utilEnum.propertyName.NAVIGATION_PAGE;
        break;
      case 'detailPage':
        propName = utilEnum.propertyName.NAVIGATION_DETAIL_PAGE;
        break;
      case 'gridColumnDetailPage':
        propName = utilEnum.propertyName.GRID_COLUMN_NAVIGATION_PAGE;
        break;
      case 'quickfilterpvinputmapping':
      case 'filterfieldpvinputmapping':
        propName = utilEnum.propertyName.QUICK_FILTER_INPUT_MAPPING;
        break;
      case 'quickfilterpvoutputmapping':
      case 'filterfieldpvoutputmapping':
        propName = utilEnum.propertyName.QUICK_FILTER_OUTPUT_MAPPING;
        break;
      case 'tablesearchpvinputmapping':
      case 'searchfieldpvinputmapping':
        propName = utilEnum.propertyName.TBL_SEARCH_INPUT_MAPPING;
        break;
      case 'tablesearchpvoutputmapping':
      case 'searchfieldpvoutputmapping':
        propName = utilEnum.propertyName.TBL_SEARCH_OUTPUT_MAPPING;
        break;
      case 'formfieldpvinputmapping':
        propName = utilEnum.propertyName.FORM_FIELD_INPUT_MAPPING;
        break;
      case 'formfieldpvoutputmapping':
        propName = utilEnum.propertyName.FORM_FIELD_OUTPUT_MAPPING;
        break;
      case 'standarddatagridpvparametermapping':
        propName = utilEnum.propertyName.STANDARD_GRID_PARAMETER_MAPPING;
        break;
      case 'queryviewpvqueryviewmapping':
        propName = utilEnum.propertyName.EMBEDDED_QUERY_VIEW_MAPPING;
        break;
      case 'gridpvparametermappingforquerylist':
        propName = utilEnum.propertyName.QUERY_PARAMETER_MAPPING;
        break;
      case 'gridpvdetailpagemapping':
        propName = utilEnum.propertyName.QUERY_DETAIL_PAGE_MAPPING;
        break;
      case 'gridcolumnpvdetailviewparams':
        propName = utilEnum.propertyName.QUERY_DETAIL_VIEW_MAPPING;
        break;
      case 'gridcolumnpvholdvalueparams':
        propName = utilEnum.propertyName.QUERY_HOLD_VIEW_VALUES;
        break;
      case 'gridpvdetailpageparameters':
        propName = utilEnum.propertyName.DETAIL_PAGE_PARAMETERS;
        break;
      case 'menuitem':
      case 'menu_item':
      case 'tabitem':
      case 'hometile':
      case 'menugroup':
      case 'menu_group':
      case 'tabgroup':
      case 'button':
        propName = utilEnum.propertyName.NAVIGATION_PAGE;
        break;
      default:
        propName = utilEnum.propertyName.NAVIGATION_PAGE;
        break;
    }
    return propName;
  }

  /**
   * Gets the property name based on the property and origin name (for mappings)
   */
  getPropNameBasedOnProp(prop: string, originName?: string): string {
    let propName;

    const getMappingPropName = (mappingType: string, originName?: string) => {
      switch (originName) {
        case 'lookupField':
        case 'listField':
        case 'sourceKey':
          return utilEnum.propertyName[`${mappingType}_SOURCE_FIELD`];
        case 'tableField':
        case 'detailField':
        case 'targetKey':
          return utilEnum.propertyName[`${mappingType}_TARGET_FIELD`];
        default:
          return utilEnum.propertyName[mappingType];
      }
    };

    switch (prop) {
      case 'inputMapping':
        propName = getMappingPropName('PAGES_INPUT_MAPPING', originName);
        break;
      case 'outputMapping':
        propName = getMappingPropName('PAGES_OUTPUT_MAPPING', originName);
        break;
      case 'queryViewMapping':
        propName = getMappingPropName('QUERY_VIEW_MAPPING', originName);
        break;
      case 'detailPageMapping':
        propName = getMappingPropName('DETAIL_PAGE_MAPPING', originName);
        break;
      case 'parameterMapping':
        propName = getMappingPropName('PARAMETER_MAPPING', originName);
        break;
      case 'holdValueParams':
        propName = getMappingPropName('HOLD_VALUE_PARAMS_MAPPING', originName);
        break;
      case 'detailViewParams':
        propName = getMappingPropName('DETAIL_VIEW_PARAMS_MAPPING', originName);
        break;
      case 'parameterMappingForQueryList':
        propName = getMappingPropName('PARAMETER_MAPPING_QUERY_VIEW', originName);
        break;
      case 'detailPageParameters':
        propName = getMappingPropName('DETAIL_PAGE_PARAMETERS', originName);
        break;
      case 'queryViewMappingPageVariables':
        propName = getMappingPropName('QUERY_VIEW_MAPPING_PAGE_VARIABLES', originName);
        break;
      case 'parameterMappingPageVariables':
        propName = getMappingPropName('STANDARD_GRID_PARAMETER_MAPPING_PAGE_VARIABLES', originName);
        break;
      case 'navigatePageValueMapping':
        propName = getMappingPropName('NAVIGATION_DATA_MAPPING', originName);
        break;
      case 'executeOnFldMapping':
        propName = getMappingPropName('EXECUTE_ON_FIELD_MAPPING', originName);
        break;
      case 'executeOnPgVarMapping':
        propName = getMappingPropName('EXECUTE_ON_PAGE_VARIABLE_MAPPING', originName);
        break;
      default:
        break;
    }
    return propName;
  }

  /**
   * Gets newly added fields by comparing old and new arrays
   */
  getNewlyAddedFlds(oldFlds: any[], newFlds: any[]): any[] {
    if (!oldFlds) {
      oldFlds = [];
    }
    if (!newFlds) {
      newFlds = [];
    }
    if (newFlds?.length > oldFlds?.length) {
      return newFlds.filter((n) => !oldFlds.includes(n));
    }
    return [];
  }

  /**
   * Creates or deletes references for mapping changes
   */
  createOrDelRefForMapping(
    oldDt: any[] | null,
    newDT: any[] | null,
    mapName: string,
    node: any,
    propNme: string,
    adInfo?: any,
    lkMap?: any,
    configurationId?: string,
    context?: ReferenceContext
  ): void {
    const pageVariableFldsNew = newDT?.filter((f) => f[propNme]?.type === 'pageVariable').map((fd) => fd[propNme]?.sid);
    const path = this.getAdditionalPath(node);

    const getSourceFieldSid = (entry: any): string => {
      return entry?.lookupField?.sid || entry?.listField?.sid || entry?.sourceKey?.sid || '';
    };

    const getCompositeKey = (entry: any): string => {
      const sourceSid = getSourceFieldSid(entry);
      const targetSid = entry?.[propNme]?.sid || '';
      return `${sourceSid}::${targetSid}`;
    };

    // Find entries to add (in new but not in old)
    const usedOldIndices: number[] = [];
    const entriesToAdd: any[] = [];
    newDT?.forEach((newEntry) => {
      const newKey = getCompositeKey(newEntry);
      const matchingOldIndex = oldDt?.findIndex(
        (oldEntry, oldIndex) => !usedOldIndices.includes(oldIndex) && getCompositeKey(oldEntry) === newKey
      );
      if (matchingOldIndex === -1 || matchingOldIndex === undefined) {
        entriesToAdd.push(newEntry);
      } else {
        usedOldIndices.push(matchingOldIndex);
      }
    });

    // Find entries to remove (in old but not in new)
    const usedNewIndices: number[] = [];
    const entriesToRemove: any[] = [];
    oldDt?.forEach((oldEntry) => {
      const oldKey = getCompositeKey(oldEntry);
      const matchingNewIndex = newDT?.findIndex(
        (newEntry, newIndex) => !usedNewIndices.includes(newIndex) && getCompositeKey(newEntry) === oldKey
      );
      if (matchingNewIndex === -1 || matchingNewIndex === undefined) {
        entriesToRemove.push(oldEntry);
      } else {
        usedNewIndices.push(matchingNewIndex);
      }
    });

    // Process additions
    entriesToAdd?.forEach((mappingEntry) => {
      const f = mappingEntry?.[propNme]?.sid;
      if (!f) return;

      let sourceFieldId;
      let currentAdInfo = adInfo;
      const referredAppId =
        propNme === 'lookupField' && (mapName === 'outputMapping' || mapName === 'inputMapping')
          ? mappingEntry?.[propNme]?.appId || context?.appId
          : context?.appId;

      if (
        (propNme === 'tableField' || propNme === 'detailField' || propNme === 'targetKey') &&
        (mapName === 'outputMapping' ||
          mapName === 'inputMapping' ||
          mapName === 'detailPageMapping' ||
          mapName === 'queryViewMapping' ||
          mapName === 'parameterMapping' ||
          mapName === 'detailViewParams' ||
          mapName === 'detailPageParameters' ||
          mapName === 'parameterMappingPageVariables' ||
          mapName === 'queryViewMappingPageVariables' ||
          mapName === 'holdValueParams' ||
          mapName === 'parameterMappingForQueryList' ||
          mapName === 'navigatePageValueMapping' ||
          mapName === 'executeOn')
      ) {
        sourceFieldId = getSourceFieldSid(mappingEntry);
        if (sourceFieldId) {
          currentAdInfo = { ...adInfo, sourceFieldId: sourceFieldId };
        }
      }

      if (pageVariableFldsNew?.includes(f)) {
        context?.referencesTasks.addedReference.push(
          this.util.createReference(
            context?.currentSelectedItem.sid,
            utilEnum.modelType.PAGE,
            f,
            utilEnum.modelType.PAGE_VARIABLE,
            referredAppId,
            path,
            this.getPropNameBasedOnProp(mapName, propNme),
            false,
            currentAdInfo,
            sourceFieldId,
            undefined,
            configurationId
          )
        );
      } else {
        context?.referencesTasks.addedReference.push(
          this.util.createReference(
            context?.currentSelectedItem.sid,
            utilEnum.modelType.PAGE,
            f,
            utilEnum.modelType.FIELD,
            referredAppId,
            path,
            this.getPropNameBasedOnProp(mapName, propNme),
            !(
              mapName === 'parameterMappingForQueryList' ||
              propNme === 'tableField' ||
              propNme === 'lookupField' ||
              propNme === 'executeOn'
            ),
            currentAdInfo,
            sourceFieldId,
            undefined,
            configurationId
          )
        );
      }
    });

    // Process deletions
    entriesToRemove?.forEach((mappingEntry) => {
      const f = mappingEntry?.[propNme]?.sid;
      if (!f) return;

      let propertyName, sourceFieldId;
      propertyName = this.getPropNameBasedOnProp(mapName, propNme);
      const referredAppIdForDeletion =
        propNme === 'lookupField' && (mapName === 'outputMapping' || mapName === 'inputMapping')
          ? mappingEntry?.[propNme]?.appId
          : undefined;
      if (
        (propNme === 'tableField' || propNme === 'detailField' || propNme === 'targetKey') &&
        (mapName === 'outputMapping' ||
          mapName === 'inputMapping' ||
          mapName === 'detailPageMapping' ||
          mapName === 'queryViewMapping' ||
          mapName === 'parameterMapping' ||
          mapName === 'detailViewParams' ||
          mapName === 'detailPageParameters' ||
          mapName === 'parameterMappingPageVariables' ||
          mapName === 'queryViewMappingPageVariables' ||
          mapName === 'holdValueParams' ||
          mapName === 'parameterMappingForQueryList' ||
          mapName === 'navigatePageValueMapping' ||
          mapName === 'executeOn')
      ) {
        sourceFieldId = getSourceFieldSid(mappingEntry);
      }
      context?.referencesTasks.deletedReference.push(
        this.util.createObjForDelRef(
          context?.currentSelectedItem.sid,
          f,
          propertyName,
          adInfo,
          sourceFieldId,
          referredAppIdForDeletion,
          configurationId
        )
      );
    });
  }

  /**
   * Creates or deletes references for executeOn event fields
   */
  createOrDelRefForExecuteOnEventFlds(oldDt: any, newDT: any, node: any, adInfo: any, context: ReferenceContext): void {
    const oldflds = oldDt?.fieldChanges;
    const newFlds = newDT?.fieldChanges;
    const pageVariableFldsOld = oldDt?.variableChanges;
    const pageVariableFldsNew = newDT?.variableChanges;
    const fldsToRem = oldflds?.filter((f) => !newFlds?.includes(f));
    const fldsToAdd = newFlds?.filter((f) => !oldflds?.includes(f));
    const pageVariableFldsToRem = pageVariableFldsOld?.filter((f) => !pageVariableFldsNew?.includes(f));
    const pageVariableFldsToAdd = pageVariableFldsNew?.filter((f) => !pageVariableFldsOld?.includes(f));
    const path = this.getAdditionalPath(node);

    fldsToAdd?.forEach((f) => {
      context.referencesTasks.addedReference.push(
        this.util.createReference(
          context.currentSelectedItem.sid,
          utilEnum.modelType.PAGE,
          f,
          'FIELD',
          context.appId,
          path,
          this.getPropNameBasedOnProp('executeOnFldMapping'),
          false,
          adInfo
        )
      );
    });

    fldsToRem?.forEach((f) => {
      context.referencesTasks.deletedReference.push(
        this.util.createObjForDelRef(
          context.currentSelectedItem.sid,
          f,
          this.getPropNameBasedOnProp('executeOnFldMapping'),
          adInfo
        )
      );
    });

    pageVariableFldsToAdd?.forEach((f) => {
      context.referencesTasks.addedReference.push(
        this.util.createReference(
          context.currentSelectedItem.sid,
          utilEnum.modelType.PAGE,
          f,
          'PAGE_VARIABLE',
          context.appId,
          path,
          this.getPropNameBasedOnProp('executeOnPgVarMapping'),
          false,
          adInfo
        )
      );
    });

    pageVariableFldsToRem?.forEach((f) => {
      context.referencesTasks.deletedReference.push(
        this.util.createObjForDelRef(
          context.currentSelectedItem.sid,
          f,
          this.getPropNameBasedOnProp('executeOnPgVarMapping'),
          adInfo
        )
      );
    });
  }

  /**
   * Handles reference changes for button beforeAction fields deletion
   */
  handleBeforeActionFieldsDeletion(olddata: any, node: any, context: ReferenceContext): void {
    if (olddata?.fields) {
      const oldFields = olddata.fields.split(',');
      oldFields.forEach((sid) => {
        context.referencesTasks.deletedReference.push(
          this.util.createObjForDelRef(
            context.currentSelectedItem.sid,
            sid,
            utilEnum.propertyName.PAGE_FIELDS,
            { menu: node.data?.id, path: this.getAdditionalPath(node) }
          )
        );
      });
    }
  }

  /**
   * Handles reference changes for page properties (page, pageName, detailPage)
   */
  handlePageReferenceChanges(
    props: any,
    olddata: any,
    prop: string,
    node: any,
    nType: string,
    adInfo: any,
    context: ReferenceContext
  ): { resetPrps: boolean; props: any } {
    let resetPrps = false;

    // Delete old reference
    if (olddata?.[prop]) {
      const deleteResult = this.deleteOldPageReference(olddata, prop, node, nType, context);
      if (deleteResult.resetPrps) {
        resetPrps = true;
        props = { ...props, ...deleteResult.propsToReset };
      }
    }

    // Add new reference
    if (props[prop]) {
      const addResult = this.addNewPageReference(props, prop, node, adInfo, context);
      if (addResult.resetPrps) {
        resetPrps = true;
      }
      props = { ...props, ...addResult.propsToUpdate };
    }

    return { resetPrps, props };
  }

  private isMenuOrTabType(nType: string): boolean {
    return ['menuitem', 'menu_item', 'tabitem', 'hometile', 'menugroup', 'menu_group', 'tabgroup', 'button'].includes(nType);
  }

  private findPageInScreens(sid: string, context: ReferenceContext): any {
    return context.allSelectedScreens?.userGenScreens?.find((p) => p.sid === sid)
      || context.allSelectedScreens?.sysGenScreens?.find((p) => p.sid === sid);
  }

  private deleteOldPageReference(
    olddata: any,
    prop: string,
    node: any,
    nType: string,
    context: ReferenceContext
  ): { resetPrps: boolean; propsToReset: any } {
    const menuData = this.isMenuOrTabType(nType) ? { menu: node.data?.id } : undefined;

    context.referencesTasks.deletedReference.push(
      this.util.createObjForDelRef(
        context.currentSelectedItem.sid,
        olddata[prop].sid,
        this.getPropName(prop),
        menuData
      )
    );

    const pg = this.findPageInScreens(olddata[prop]?.sid, context);
    if (pg?.pageTableType !== 'QUERY_VIEW') {
      return { resetPrps: false, propsToReset: {} };
    }

    if (prop === 'detailPage') {
      return {
        resetPrps: true,
        propsToReset: {
          detailPageTableId: null,
          detailPageAppId: null,
          detailPageType: null,
          detailPageMapping: null
        }
      };
    }
    return { resetPrps: false, propsToReset: { tableId: null, appId: null } };
  }

  private addNewPageReference(
    props: any,
    prop: string,
    node: any,
    adInfo: any,
    context: ReferenceContext
  ): { resetPrps: boolean; propsToUpdate: any } {
    let manualChange = false;
    let resetPrps = false;
    let propsToUpdate: any = {};

    const pg = this.findPageInScreens(props[prop]?.sid, context);
    const newPageAppId = props[prop]?.appId || context.appId;

    if (pg && pg?.tableId !== context.currentSelectedItem.tableId) {
      manualChange = true;
      if (prop === 'detailPage') {
        resetPrps = true;
        propsToUpdate = {
          detailPageTableId: pg.tableId,
          detailPageAppId: pg.appId,
          detailPageType: 'query'
        };
      } else {
        propsToUpdate = { tableId: pg.tableId, appId: pg.appId };
      }
    }

    const propName = this.getPropName(prop);
    const additionalInfoForRef = this.buildAdditionalInfoForRef(propName, node, adInfo);

    context.referencesTasks.addedReference.push(
      this.util.createReference(
        context.currentSelectedItem.sid,
        utilEnum.modelType.PAGE,
        props[prop].sid,
        utilEnum.modelType.PAGE,
        newPageAppId,
        this.getAdditionalPath(node),
        propName,
        manualChange,
        additionalInfoForRef
      )
    );

    return { resetPrps, propsToUpdate };
  }

  private buildAdditionalInfoForRef(propName: string, node: any, adInfo: any): any {
    if (propName === utilEnum.propertyName.NAVIGATION_PAGE) {
      return { path: this.getAdditionalPath(node), menu: node.data?.id };
    }
    if (propName === utilEnum.propertyName.NAVIGATION_DETAIL_PAGE) {
      return { path: this.getAdditionalPath(node) };
    }
    return this.util.updateAdInfoForMapping(adInfo, 'detailPage');
  }

  /**
   * Handles reference changes for sortField property
   */
  handleSortFieldReferenceChanges(props: any, olddata: any, node: any, context: ReferenceContext): void {
    if (props.sortField) {
      context.referencesTasks.addedReference.push(
        this.util.createReference(
          context.currentSelectedItem.sid,
          utilEnum.modelType.PAGE,
          props.sortField.sid,
          utilEnum.modelType.FIELD,
          context.appId,
          this.getAdditionalPath(node),
          utilEnum.propertyName.DEFAULT_SORT_ON,
          false
        )
      );
    }
    if (olddata?.sortField) {
      context.referencesTasks.deletedReference.push(
        this.util.createObjForDelRef(
          context.currentSelectedItem.sid,
          olddata?.sortField.sid,
          utilEnum.propertyName.DEFAULT_SORT_ON
        )
      );
    }
  }

  /**
   * Handles reference changes for sortFields property (multi-select)
   */
  handleSortFieldsReferenceChanges(props: any, olddata: any, node: any, context: ReferenceContext): void {
    const oldGrpCols = olddata?.sortFields?.map((item) => item.sid);
    const newGrpCols = props.sortFields?.map((item) => item.sid);
    const dataTorem = oldGrpCols?.filter((f) => !newGrpCols?.includes(f));
    const dataToSave = newGrpCols?.filter((f) => !oldGrpCols?.includes(f));

    if (dataToSave?.length) {
      dataToSave.forEach((sid) => {
        context.referencesTasks.addedReference.push(
          this.util.createReference(
            context.currentSelectedItem.sid,
            utilEnum.modelType.PAGE,
            sid,
            utilEnum.modelType.FIELD,
            context.appId,
            this.getAdditionalPath(node),
            utilEnum.propertyName.DEFAULT_SORT_ON,
            false
          )
        );
      });
    } else if (dataTorem?.length) {
      dataTorem.forEach((sid) => {
        context.referencesTasks.deletedReference.push(
          this.util.createObjForDelRef(context.currentSelectedItem.sid, sid, utilEnum.propertyName.DEFAULT_SORT_ON)
        );
      });
    }
  }

  /**
   * Handles reference changes for groupOnColumn property
   */
  handleGroupOnColumnReferenceChanges(props: any, olddata: any, node: any, context: ReferenceContext): void {
    if (props.groupOnColumn) {
      context.referencesTasks.addedReference.push(
        this.util.createReference(
          context.currentSelectedItem.sid,
          utilEnum.modelType.PAGE,
          props.groupOnColumn.sid,
          utilEnum.modelType.FIELD,
          context.appId,
          this.getAdditionalPath(node),
          utilEnum.propertyName.GROUP_ON_COLUMN,
          false
        )
      );
    }
    if (olddata?.groupOnColumn) {
      context.referencesTasks.deletedReference.push(
        this.util.createObjForDelRef(
          context.currentSelectedItem.sid,
          olddata?.groupOnColumn.sid,
          utilEnum.propertyName.GROUP_ON_COLUMN
        )
      );
    }
  }

  /**
   * Handles reference changes for groupsOnColumn property (comma-separated)
   */
  handleGroupsOnColumnReferenceChanges(props: any, olddata: any, node: any, context: ReferenceContext): void {
    const oldGrpCols = olddata?.groupsOnColumn?.split(',');
    const newGrpCols = props.groupsOnColumn?.split(',');
    const dataTorem = oldGrpCols?.filter((f) => !newGrpCols?.includes(f));
    const dataToSave = newGrpCols?.filter((f) => !oldGrpCols?.includes(f));

    if (dataToSave?.length) {
      dataToSave.forEach((sid) => {
        context.referencesTasks.addedReference.push(
          this.util.createReference(
            context.currentSelectedItem.sid,
            utilEnum.modelType.PAGE,
            sid,
            utilEnum.modelType.FIELD,
            context.appId,
            this.getAdditionalPath(node),
            utilEnum.propertyName.GROUPS_ON_COLUMN,
            false
          )
        );
      });
    } else if (dataTorem?.length) {
      dataTorem.forEach((sid) => {
        context.referencesTasks.deletedReference.push(
          this.util.createObjForDelRef(
            context.currentSelectedItem.sid,
            sid,
            utilEnum.propertyName.GROUPS_ON_COLUMN
          )
        );
      });
    }
  }

  /**
   * Handles reference changes for fields property
   */
  handleFieldsReferenceChanges(props: any, olddata: any, node: any, context: ReferenceContext): void {
    const oldFields = olddata?.fields?.split(',');
    const newFields = props.fields?.split(',');
    const dataTorem = oldFields?.filter((f) => !newFields?.includes(f));
    const dataToSave = newFields?.filter((f) => !oldFields?.includes(f));

    if (dataToSave?.length) {
      dataToSave.forEach((sid) => {
        context.referencesTasks.addedReference.push(
          this.util.createReference(
            context.currentSelectedItem.sid,
            utilEnum.modelType.PAGE,
            sid,
            utilEnum.modelType.FIELD,
            context.appId,
            this.getAdditionalPath(node),
            utilEnum.propertyName.PAGE_FIELDS,
            false,
            { menu: node.data?.id, path: this.getAdditionalPath(node) }
          )
        );
      });
    }

    if (dataTorem?.length) {
      dataTorem.forEach((sid) => {
        context.referencesTasks.deletedReference.push(
          this.util.createObjForDelRef(
            context.currentSelectedItem.sid,
            sid,
            utilEnum.propertyName.PAGE_FIELDS,
            { menu: node.data?.id, path: this.getAdditionalPath(node) }
          )
        );
      });
    }
  }

  /**
   * Handles reference changes for service property
   */
  handleServiceReferenceChanges(props: any, olddata: any, node: any, context: ReferenceContext): void {
    if (props.service) {
      context.referencesTasks.addedReference.push(
        this.util.createReference(
          context.currentSelectedItem.sid,
          utilEnum.modelType.PAGE,
          props.service.sid,
          utilEnum.modelType.SERVICE,
          context.appId,
          this.getAdditionalPath(node),
          utilEnum.propertyName.SERVICE,
          false,
          { menu: node.data?.id }
        )
      );
      if (props.service.apiGroupId) {
        context.referencesTasks.addedReference.push(
          this.util.createReference(
            context.currentSelectedItem.sid,
            utilEnum.modelType.PAGE,
            props.service.apiGroupId,
            utilEnum.modelType.APIGROUP,
            context.appId,
            this.getAdditionalPath(node),
            utilEnum.propertyName.APIGROUP_SERVICE_BUTTON,
            false,
            { menu: node.data?.id }
          )
        );
      }
    }

    if (olddata?.service) {
      context.referencesTasks.deletedReference.push(
        this.util.createObjForDelRef(
          context.currentSelectedItem.sid,
          olddata?.service.sid,
          utilEnum.propertyName.SERVICE,
          { menu: node.data?.id }
        )
      );
      if (olddata?.service.apiGroupId) {
        context.referencesTasks.deletedReference.push(
          this.util.createObjForDelRef(
            context.currentSelectedItem.sid,
            olddata?.service.apiGroupId,
            utilEnum.propertyName.APIGROUP_SERVICE_BUTTON,
            { menu: node.data?.id }
          )
        );
      }
    }
  }

  /**
   * Handles reference changes for alignColumns property
   */
  handleAlignColumnsReferenceChanges(props: any, olddata: any, node: any, context: ReferenceContext): void {
    const oldAlignCols = olddata?.alignColumns?.raw?.map((r) => r.id);
    const newAligncols = props.alignColumns?.raw?.map((r) => r.id);
    const dataTorem = oldAlignCols?.filter((f) => !newAligncols?.includes(f));
    const dataToSave = newAligncols?.filter((f) => !oldAlignCols?.includes(f));

    if (dataToSave?.length) {
      dataToSave.forEach((sid) => {
        context.referencesTasks.addedReference.push(
          this.util.createReference(
            context.currentSelectedItem.sid,
            utilEnum.modelType.PAGE,
            sid,
            utilEnum.modelType.FIELD,
            context.appId,
            this.getAdditionalPath(node),
            utilEnum.propertyName.ALIGN_COLUMNS,
            false
          )
        );
      });
    }

    if (dataTorem?.length) {
      dataTorem.forEach((sid) => {
        context.referencesTasks.deletedReference.push(
          this.util.createObjForDelRef(context.currentSelectedItem.sid, sid, utilEnum.propertyName.ALIGN_COLUMNS)
        );
      });
    }
  }

  /**
   * Handles reference changes for accessControl (roles) property
   */
  handleAccessControlReferenceChanges(props: any, olddata: any, node: any, nType: string, context: ReferenceContext): void {
    const { oldAcc, newAcc } = this.normalizeAccessControlValues(olddata, props, context);
    const rolesTorem = this.getRolesToRemove(oldAcc, newAcc);
    const rolesToSave = newAcc?.filter((f) => !oldAcc?.includes(f));

    this.addAccessControlReferences(rolesToSave, node, context);
    this.removeAccessControlReferences(rolesTorem, node, nType, context);
  }

  private normalizeAccessControlValues(
    olddata: any,
    props: any,
    context: ReferenceContext
  ): { oldAcc: any; newAcc: any } {
    let oldAcc = olddata?.accessControl == null ? null : (olddata?.accessControl ? [...olddata.accessControl] : []);
    let newAcc = [...props.accessControl];

    if (oldAcc?.includes('all') || newAcc.includes('all')) {
      const roles = context.allRoles?.map((role) => role.sid) || [];
      if (oldAcc?.includes('all')) oldAcc = roles;
      if (newAcc.includes('all')) newAcc = roles;
    }
    return { oldAcc, newAcc };
  }

  private getRolesToRemove(oldAcc: any, newAcc: any): any {
    return oldAcc == null ? null : oldAcc?.filter((f) => !newAcc?.includes(f));
  }

  private addAccessControlReferences(rolesToSave: any[], node: any, context: ReferenceContext): void {
    if (!rolesToSave?.length) return;
    rolesToSave.filter((sid) => sid !== 'all').forEach((sid) => {
      context.referencesTasks.addedReference.push(
        this.util.createReference(
          context.currentSelectedItem.sid,
          utilEnum.modelType.PAGE,
          sid,
          utilEnum.modelType.ROLE,
          context.allRoles?.[0]?.appId,
          this.getAdditionalPath(node),
          utilEnum.propertyName.ACCESS_CONTROL,
          undefined,
          { menu: node.data?.id }
        )
      );
    });
  }

  private removeAccessControlReferences(rolesTorem: any[], node: any, nType: string, context: ReferenceContext): void {
    if (!rolesTorem?.length || rolesTorem === null) return;
    const isMenuType = this.isMenuOrTabType(nType) || nType === 'hometile';
    rolesTorem.forEach((sid) => {
      const menuData = isMenuType ? { menu: node.data?.id } : undefined;
      context.referencesTasks.deletedReference.push(
        this.util.createObjForDelRef(
          context.currentSelectedItem.sid,
          sid,
          utilEnum.propertyName.ACCESS_CONTROL,
          menuData
        )
      );
    });
  }

  /**
   * Handles reference changes for conditional button properties
   */
  handleConditionalButtonReferenceChanges(props: any, olddata: any, node: any, context: ReferenceContext): void {
    // conditionForButtonEnable
    if (
      (props.conditionForButtonEnable || olddata?.conditionForButtonEnable) &&
      props.conditionForButtonEnable !== olddata?.conditionForButtonEnable
    ) {
      if (props.conditionForButtonEnable) {
        context.referencesTasks.addedReference.push(
          this.util.createReference(
            context.currentSelectedItem.sid,
            utilEnum.modelType.PAGE,
            JSON.parse(props.conditionForButtonEnable).sid,
            utilEnum.modelType.QUERY_BUILDER,
            context.appId,
            this.getAdditionalPath(node),
            utilEnum.propertyName.BUTTON_ENABLED_CONDITIONAL,
            true,
            { path: this.getAdditionalPath(node), menu: node.data?.id }
          )
        );
      }
      if (olddata?.conditionForButtonEnable) {
        context.referencesTasks.deletedReference.push(
          this.util.createObjForDelRef(
            context.currentSelectedItem.sid,
            JSON.parse(olddata?.conditionForButtonEnable).sid,
            utilEnum.propertyName.BUTTON_ENABLED_CONDITIONAL,
            { path: this.getAdditionalPath(node), menu: node.data?.id }
          )
        );
      }
    }

    // conditionForButtonVisiblity
    if (
      (props.conditionForButtonVisiblity || olddata?.conditionForButtonVisiblity) &&
      props.conditionForButtonVisiblity !== olddata?.conditionForButtonVisiblity
    ) {
      if (props.conditionForButtonVisiblity) {
        context.referencesTasks.addedReference.push(
          this.util.createReference(
            context.currentSelectedItem.sid,
            utilEnum.modelType.PAGE,
            JSON.parse(props.conditionForButtonVisiblity).sid,
            utilEnum.modelType.QUERY_BUILDER,
            context.appId,
            this.getAdditionalPath(node),
            utilEnum.propertyName.BUTTON_VISIBILITY_CONDITIONAL,
            true,
            { path: this.getAdditionalPath(node), menu: node.data?.id }
          )
        );
      }
      if (olddata?.conditionForButtonVisiblity) {
        context.referencesTasks.deletedReference.push(
          this.util.createObjForDelRef(
            context.currentSelectedItem.sid,
            JSON.parse(olddata?.conditionForButtonVisiblity).sid,
            utilEnum.propertyName.BUTTON_VISIBILITY_CONDITIONAL,
            { path: this.getAdditionalPath(node), menu: node.data?.id }
          )
        );
      }
    }
  }

  /**
   * Handles reference changes for action property (custom actions)
   */
  handleActionReferenceChanges(props: any, olddata: any, node: any, context: ReferenceContext): void {
    if (props.action && props.action.includes('-')) {
      context.referencesTasks.addedReference.push(
        this.util.createReference(
          context.currentSelectedItem.sid,
          utilEnum.modelType.PAGE,
          props.action,
          utilEnum.modelType.ACL_CUSTOM_ACTION,
          context.appId,
          this.getAdditionalPath(node),
          utilEnum.propertyName.PAGE_ACTION,
          false,
          { menu: node.data?.id }
        )
      );
    }
    if (olddata?.action && olddata?.action.includes('-')) {
      context.referencesTasks.deletedReference.push(
        this.util.createObjForDelRef(
          context.currentSelectedItem.sid,
          olddata?.action,
          utilEnum.propertyName.PAGE_ACTION,
          { menu: node.data?.id }
        )
      );
    }
  }

  /**
   * Handles reference changes for allowEditing conditional property
   */
  handleAllowEditingReferenceChanges(data: any, props: any, olddata: any, node: any, context: ReferenceContext): void {
    if (data.allowEditing === 'conditional') {
      if (data.allowEditingCondition && !olddata.allowEditingCondition) {
        context.referencesTasks.addedReference.push(
          this.util.createReference(
            context.currentSelectedItem.sid,
            utilEnum.modelType.PAGE,
            JSON.parse(props.allowEditingCondition).sid,
            utilEnum.modelType.QUERY_BUILDER,
            context.appId,
            this.getAdditionalPath(node),
            utilEnum.propertyName.EDIT_CONDITIONAL,
            true,
            this.util.enrichAdInfo(
              { path: this.getAdditionalPath(node), menu: node.data?.id },
              { props: props, node: node }
            )
          )
        );
      }
      if (!data.allowEditingCondition && olddata.allowEditingCondition) {
        context.referencesTasks.deletedReference.push(
          this.util.createObjForDelRef(
            context.currentSelectedItem.sid,
            JSON.parse(olddata?.allowEditingCondition).sid,
            utilEnum.propertyName.EDIT_CONDITIONAL,
            this.util.enrichAdInfo({ menu: node.data?.id }, { props: props, node: node })
          )
        );
      }
    } else {
      if (data.allowEditingCondition) {
        context.referencesTasks.deletedReference.push(
          this.util.createObjForDelRef(
            context.currentSelectedItem.sid,
            JSON.parse(olddata?.allowEditingCondition).sid,
            utilEnum.propertyName.EDIT_CONDITIONAL,
            this.util.enrichAdInfo({ menu: node.data?.id }, { props: props, node: node })
          )
        );
      }
    }
  }

  /**
   * Handles reference changes for allowViewing conditional property
   */
  handleAllowViewingReferenceChanges(data: any, props: any, olddata: any, node: any, context: ReferenceContext): void {
    if (data.allowViewing === 'conditional') {
      if (data.allowViewingCondition && !olddata.allowViewingCondition) {
        context.referencesTasks.addedReference.push(
          this.util.createReference(
            context.currentSelectedItem.sid,
            utilEnum.modelType.PAGE,
            JSON.parse(props.allowViewingCondition).sid,
            utilEnum.modelType.QUERY_BUILDER,
            context.appId,
            this.getAdditionalPath(node),
            utilEnum.propertyName.VIEW_CONDITIONAL,
            true,
            this.util.enrichAdInfo(
              { path: this.getAdditionalPath(node), menu: node.data?.id },
              { props: props, node: node }
            )
          )
        );
      }
      if (!data.allowViewingCondition && olddata.allowViewingCondition) {
        context.referencesTasks.deletedReference.push(
          this.util.createObjForDelRef(
            context.currentSelectedItem.sid,
            JSON.parse(olddata?.allowViewingCondition).sid,
            utilEnum.propertyName.VIEW_CONDITIONAL,
            this.util.enrichAdInfo({ menu: node.data?.id }, { props: props, node: node })
          )
        );
      }
    } else {
      if (data.allowViewingCondition) {
        context.referencesTasks.deletedReference.push(
          this.util.createObjForDelRef(
            context.currentSelectedItem.sid,
            JSON.parse(olddata?.allowViewingCondition).sid,
            utilEnum.propertyName.VIEW_CONDITIONAL,
            this.util.enrichAdInfo({ menu: node.data?.id }, { props: props, node: node })
          )
        );
      }
    }
  }

  /**
   * Handles reference changes for lookupConfiguration property
   */
  handleLookupConfigurationReferenceChanges(
    props: any,
    olddata: any,
    node: any,
    adInfo: any,
    context: ReferenceContext
  ): any[] {
    const oldlk = olddata?.lookupConfiguration;
    const newlk = props.lookupConfiguration;
    let screenLookupDefinitionDetails: any[] = [];

    this.handleLookupToForeignKeyChanges(newlk, oldlk, node, adInfo, context);
    this.handleLookupToQueryViewChanges(newlk, oldlk, node, adInfo, context);
    screenLookupDefinitionDetails = this.handleLookupDefinitionChanges(newlk, oldlk, node, adInfo, context);
    this.handleLookupOutputMappingChanges(newlk, oldlk, node, adInfo, context);
    this.handleLookupInputMappingChanges(newlk, oldlk, node, adInfo, context);
    this.handleLookupExecuteOnChanges(newlk, oldlk, node, adInfo, context);

    return screenLookupDefinitionDetails;
  }

  private handleLookupToForeignKeyChanges(newlk: any, oldlk: any, node: any, adInfo: any, context: ReferenceContext): void {
    if (!this.hasLookupPropertyChanged(newlk?.lookupToForeignkey, oldlk?.lookupToForeignkey)) return;

    if (newlk?.lookupToForeignkey) {
      const fk = context.foreignKeys?.find((d) => d.sid === newlk.lookupToForeignkey);
      context.referencesTasks.addedReference.push(
        this.util.createReference(
          context.currentSelectedItem.sid, utilEnum.modelType.PAGE, newlk.lookupToForeignkey,
          utilEnum.modelType.FOREIGN_KEY, fk?.appId, this.getAdditionalPath(node),
          utilEnum.propertyName.LOOKUP_TO_FOREIGN_KEY, true,
          this.util.updateAdInfoForLookup(adInfo, 'LOOKUP_TO_FOREIGNKEY'), undefined
        )
      );
    }
    if (oldlk?.lookupToForeignkey) {
      const fk = context.foreignKeys?.find((d) => d.sid === oldlk.lookupToForeignkey);
      context.referencesTasks.deletedReference.push(
        this.util.createObjForDelRef(
          context.currentSelectedItem.sid, oldlk?.lookupToForeignkey,
          utilEnum.propertyName.LOOKUP_TO_FOREIGN_KEY,
          this.util.updateAdInfoForLookup(adInfo, 'LOOKUP_TO_FOREIGNKEY'), undefined, fk?.appId
        )
      );
    }
  }

  private handleLookupToQueryViewChanges(newlk: any, oldlk: any, node: any, adInfo: any, context: ReferenceContext): void {
    if (!this.hasLookupPropertyChanged(newlk?.lookupToQueryView, oldlk?.lookupToQueryView)) return;

    if (newlk?.lookupToQueryView) {
      context.referencesTasks.addedReference.push(
        this.util.createReference(
          context.currentSelectedItem.sid, utilEnum.modelType.PAGE, newlk.lookupToQueryView,
          utilEnum.modelType.TABLE, newlk.appId || context.appId, this.getAdditionalPath(node),
          utilEnum.propertyName.LOOKUP_TO_QUERY_VIEW, true,
          this.util.updateAdInfoForLookup({ ...adInfo, menu: node.data?.id }, 'LOOKUP_TO_QUERYVIEW'), undefined
        )
      );
    }
    if (oldlk?.lookupToQueryView) {
      context.referencesTasks.deletedReference.push(
        this.util.createObjForDelRef(
          context.currentSelectedItem.sid, oldlk?.lookupToQueryView,
          utilEnum.propertyName.LOOKUP_TO_QUERY_VIEW,
          this.util.updateAdInfoForLookup({ ...adInfo, menu: node.data?.id }, 'LOOKUP_TO_QUERYVIEW'),
          undefined, oldlk?.appId || context.appId
        )
      );
    }
  }

  private handleLookupDefinitionChanges(newlk: any, oldlk: any, node: any, adInfo: any, context: ReferenceContext): any[] {
    if (!this.hasLookupPropertyChanged(newlk?.lookupDefinition, oldlk?.lookupDefinition)) return [];

    let defDetails = this.buildLookupDefinitionDetails(newlk, oldlk, context);

    if (newlk?.lookupDefinition) {
      context.referencesTasks.addedReference.push(
        this.util.createReference(
          context.currentSelectedItem.sid, utilEnum.modelType.PAGE, newlk.lookupDefinition,
          utilEnum.modelType.LOOKUP_TEMPLATE, newlk?.appId || context.appId, this.getAdditionalPath(node),
          utilEnum.propertyName.LOOKUP_DEFINITION, false,
          this.util.updateAdInfoForLookup({ ...adInfo, menu: node.data?.id }, 'LOOKUP_DEFINITION'), undefined
        )
      );
    }
    if (oldlk?.lookupDefinition) {
      context.referencesTasks.deletedReference.push(
        this.util.createObjForDelRef(
          context.currentSelectedItem.sid, oldlk?.lookupDefinition,
          utilEnum.propertyName.LOOKUP_DEFINITION, { menu: node.data?.id }, undefined, oldlk?.appId || context.appId
        )
      );
    }
    return defDetails ? [{ ...defDetails }] : [];
  }

  private buildLookupDefinitionDetails(newlk: any, oldlk: any, context: ReferenceContext): any {
    if (newlk?.lookupDefinition) {
      const action = oldlk?.lookupDefinition ? GlobalConstants.update : GlobalConstants.create;
      return {
        lookupDefinitionId: newlk.lookupDefinition,
        oldLookupDefinitionId: oldlk?.lookupDefinition,
        action,
        referredAppId: newlk?.appId || context.appId,
        referredTableId: newlk?.tableId
      };
    }
    if (oldlk?.lookupDefinition && !newlk?.lookupDefinition) {
      return {
        lookupDefinitionId: oldlk.lookupDefinition,
        referredAppId: oldlk.appId || context.appId,
        referredTableId: oldlk.tableId,
        action: GlobalConstants.delete
      };
    }
    return null;
  }

  private handleLookupOutputMappingChanges(newlk: any, oldlk: any, node: any, adInfo: any, context: ReferenceContext): void {
    if (!(newlk?.outputMapping || oldlk?.outputMapping) || this.isEqual(newlk?.outputMapping, oldlk?.outputMapping)) return;

    this.createOrDelRefForMapping(oldlk?.outputMapping, newlk?.outputMapping, 'outputMapping', node, 'tableField',
      this.util.updateAdInfoForLookup(adInfo, 'tableField', 'outputMapping'), undefined, undefined, context);
    this.createOrDelRefForMapping(oldlk?.outputMapping, newlk?.outputMapping, 'outputMapping', node, 'lookupField',
      this.util.updateAdInfoForLookup(adInfo, 'lookupField', 'outputMapping'), undefined, undefined, context);
  }

  private handleLookupInputMappingChanges(newlk: any, oldlk: any, node: any, adInfo: any, context: ReferenceContext): void {
    const isQueryView = oldlk?.lookupType === lookupTypes.QUERY_VIEW || newlk?.lookupType === lookupTypes.QUERY_VIEW;
    if (!(newlk?.inputMapping || oldlk?.inputMapping) || !isQueryView || this.isEqual(newlk?.inputMapping, oldlk?.inputMapping)) return;

    this.createOrDelRefForMapping(oldlk?.inputMapping, newlk?.inputMapping, 'inputMapping', node, 'tableField',
      this.util.updateAdInfoForLookup(adInfo, 'tableField', 'inputMapping'), undefined, undefined, context);
    this.createOrDelRefForMapping(oldlk?.inputMapping, newlk?.inputMapping, 'inputMapping', node, 'lookupField',
      this.util.updateAdInfoForLookup(adInfo, 'lookupField', 'inputMapping'), undefined, undefined, context);
  }

  private handleLookupExecuteOnChanges(newlk: any, oldlk: any, node: any, adInfo: any, context: ReferenceContext): void {
    const isQueryView = oldlk?.lookupType === lookupTypes.QUERY_VIEW || newlk?.lookupType === lookupTypes.QUERY_VIEW;
    if (!(newlk?.executeOn || oldlk?.executeOn) || !isQueryView || this.isEqual(newlk?.executeOn, oldlk?.executeOn)) return;

    this.createOrDelRefForExecuteOnEventFlds(oldlk?.executeOn, newlk?.executeOn, node,
      this.util.updateAdInfoForLookup(adInfo, 'EXECUTE_ON_MAPPING'), context);
  }

  private hasLookupPropertyChanged(newVal: any, oldVal: any): boolean {
    return (newVal || oldVal) && newVal !== oldVal;
  }

  /**
   * Handles reference changes for gridColumnDetailPage property
   */
  handleGridColumnDetailPageReferenceChanges(props: any, olddata: any, node: any, adInfo: any, context: ReferenceContext): void {
    if (olddata?.gridColumnDetailPage) {
      context.referencesTasks.deletedReference.push(
        this.util.createObjForDelRef(
          context.currentSelectedItem.sid,
          olddata?.gridColumnDetailPage.sid,
          this.getPropName('gridColumnDetailPage'),
          { menu: node.data?.id }
        )
      );
    }
    if (props.gridColumnDetailPage) {
      context.referencesTasks.addedReference.push(
        this.util.createReference(
          context.currentSelectedItem.sid,
          utilEnum.modelType.PAGE,
          props.gridColumnDetailPage.sid,
          utilEnum.modelType.PAGE,
          context.appId,
          this.getAdditionalPath(node),
          this.getPropName('gridColumnDetailPage'),
          false,
          this.util.updateAdInfoForMapping({ ...adInfo, menu: node.data?.id }, 'gridColumnDetailPage')
        )
      );
    }
  }

  /**
   * Simple equality check for arrays/objects (using JSON.stringify)
   * Note: For more complex comparisons, use lodash isEqual
   */
  private isEqual(a: any, b: any): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
  }

  /**
   * Main entry point for processing all reference changes in a single call.
   * This consolidates all reference-related property change handling.
   */
  processAllReferenceChanges(
    input: PropertyChangeInput,
    context: ReferenceContext
  ): PropertyChangeResult {
    const { data, olddata, node, nType, adInfo, queryViewConfigurationId, stdGridConfigurationId, hasPropertyChanged, tree } = input;
    let { props } = input;
    let resetPrps = false;
    let screenLookupDefinitionDetails: any[] = [];

    // Handle button-specific changes
    props = this.handleButtonSpecificChanges(props, olddata, node, hasPropertyChanged, context);

    // Handle page reference changes
    const pageResult = this.processPageReferenceChanges(props, olddata, node, nType, adInfo, context);
    props = pageResult.props;
    resetPrps = pageResult.resetPrps || resetPrps;

    // Handle single field reference changes
    this.processSingleFieldReferenceChanges(props, olddata, node, context);

    // Handle groupsOnColumn and fields with info updates
    this.processGroupsOnColumnChanges(props, olddata, node, context);
    this.processFieldsChanges(props, olddata, node, tree, context);

    // Handle access control and conditional buttons
    this.processAccessAndConditionalChanges(props, olddata, node, nType, context);

    // Handle action and editing changes
    this.processActionAndEditingChanges(data, props, olddata, node, context);

    // Handle lookup configuration
    screenLookupDefinitionDetails = this.processLookupChanges(props, olddata, node, adInfo, context);

    // Handle mapping properties
    this.processMappingProperties(props, olddata, node, adInfo, queryViewConfigurationId, stdGridConfigurationId, context);

    // Handle page variable mappings
    this.processPageVariableMappings(props, olddata, node, adInfo, queryViewConfigurationId, stdGridConfigurationId, context);

    // Handle detail page parameters
    props = this.processDetailPageParameters(props, olddata, node, adInfo, context);

    // Handle navigate and query list mappings
    this.processNavigateAndQueryListMappings(props, olddata, node, adInfo, context);

    return { props, resetPrps, screenLookupDefinitionDetails };
  }

  private handleButtonSpecificChanges(
    props: any, olddata: any, node: any,
    hasPropertyChanged: (olddata: any, props: any, prop: string) => boolean,
    context: ReferenceContext
  ): any {
    if (node.type !== 'button') return props;

    if (hasPropertyChanged(olddata, props, 'action')) {
      props.pageName = null;
      props.navigatePageValueMapping = null;
      props.service = null;
    }
    if (hasPropertyChanged(olddata, props, 'beforeAction')) {
      this.handleBeforeActionFieldsDeletion(olddata, node, context);
      props.fields = null;
    }
    return props;
  }

  private processPageReferenceChanges(
    props: any, olddata: any, node: any, nType: string, adInfo: any, context: ReferenceContext
  ): { props: any; resetPrps: boolean } {
    let resetPrps = false;
    const prop = this.getPagePropName(props);
    if (!prop) return { props, resetPrps };

  // Send only sid, name and appId to the request
    if (props[prop]) {
      props[prop] = { sid: props[prop]?.sid, name: props[prop]?.name, appId: props[prop]?.appId };
    }
    // Compare only the sid of the pages
    if ((props[prop] || olddata?.[prop]) && props[prop]?.sid !== olddata?.[prop]?.sid) {
      const pageRefResult = this.handlePageReferenceChanges(props, olddata, prop, node, nType, adInfo, context);
      resetPrps = pageRefResult.resetPrps || resetPrps;
      props = pageRefResult.props;

      // If there is a change in navigate page, remove the navigatePageValueMapping mapping
      if (node.type === 'button' && (props.pageName || olddata?.pageName) && !isEqual(props.pageName, olddata?.pageName)) {
        props.navigatePageValueMapping = null;
        resetPrps = true;
      }
    }
    return { props, resetPrps };
  }

  private getPagePropName(props: any): string {
    if (Object.prototype.hasOwnProperty.call(props, 'page')) return 'page';
    if (Object.prototype.hasOwnProperty.call(props, 'pageName')) return 'pageName';
    if (Object.prototype.hasOwnProperty.call(props, 'detailPage')) return 'detailPage';
    return '';
  }

  private processSingleFieldReferenceChanges(props: any, olddata: any, node: any, context: ReferenceContext): void {
    if (this.hasChanged(props.sortField, olddata?.sortField)) {
      this.handleSortFieldReferenceChanges(props, olddata, node, context);
    }
    if (this.hasChanged(props.sortFields, olddata?.sortFields)) {
      this.handleSortFieldsReferenceChanges(props, olddata, node, context);
    }
    if (this.hasChanged(props.groupOnColumn, olddata?.groupOnColumn)) {
      this.handleGroupOnColumnReferenceChanges(props, olddata, node, context);
    }
    if (this.hasChanged(props.service, olddata?.service)) {
      this.handleServiceReferenceChanges(props, olddata, node, context);
    }
    if (this.hasChanged(props.alignColumns, olddata?.alignColumns)) {
      this.handleAlignColumnsReferenceChanges(props, olddata, node, context);
    }
  }

  private processGroupsOnColumnChanges(props: any, olddata: any, node: any, context: ReferenceContext): void {
    if (!this.hasChanged(props.groupsOnColumn, olddata?.groupsOnColumn)) return;

    this.handleGroupsOnColumnReferenceChanges(props, olddata, node, context);

    // Update groupsOnColumnInfo
    if (Array.isArray(context.gridChildrenFlds) && context.gridChildrenFlds.length > 0 && props.groupsOnColumn?.length) {
      props.groupsOnColumnInfo = context.gridChildrenFlds
        .filter((fld) => props.groupsOnColumn?.includes(fld.id))
        .map((fld) => ({ sid: fld.id, fkTableSid: fld.fkTableSid, fkId: fld.fkId, fkAppId: fld.fkAppId }));
    }
  }

  private processFieldsChanges(props: any, olddata: any, node: any, tree: any, context: ReferenceContext): void {
    if (!this.hasChanged(props.fields, olddata?.fields)) return;

    this.handleFieldsReferenceChanges(props, olddata, node, context);

    // Update fieldsInfo
    const gridChildren = tree?.getNodeByKey('table')?.children;
    if (Array.isArray(gridChildren) && context.gridChildrenFlds && context.gridChildrenFlds.length > 0 && props.fields?.length) {
      props.fieldsInfo = gridChildren
        .filter((fld) => props.fields?.includes(fld.key))
        .map((fld) => ({
          sid: fld.key,
          fkTableSid: fld.data?.properties?.fkTableSid,
          fkId: fld.data?.properties?.fkId,
          fkAppId: fld.data?.properties?.fkAppId
        }));
    }
  }

  private processAccessAndConditionalChanges(props: any, olddata: any, node: any, nType: string, context: ReferenceContext): void {
    if (this.hasChanged(props.accessControl, olddata?.accessControl)) {
      this.handleAccessControlReferenceChanges(props, olddata, node, nType, context);
    }
    try {
      this.handleConditionalButtonReferenceChanges(props, olddata, node, context);
    } catch (e) {
      console.error(e);
    }
  }

  private processActionAndEditingChanges(data: any, props: any, olddata: any, node: any, context: ReferenceContext): void {
    if ((props.action || olddata?.action) && props.action !== olddata?.action) {
      this.handleActionReferenceChanges(props, olddata, node, context);
    }
    this.handleAllowEditingReferenceChanges(data, props, olddata, node, context);
    this.handleAllowViewingReferenceChanges(data, props, olddata, node, context);
  }

  private processLookupChanges(props: any, olddata: any, node: any, adInfo: any, context: ReferenceContext): any[] {
    if (!this.hasChanged(props.lookupConfiguration, olddata?.lookupConfiguration)) return [];
    const lookupDefDetails = this.handleLookupConfigurationReferenceChanges(props, olddata, node, adInfo, context);
    return lookupDefDetails?.length ? lookupDefDetails : [];
  }

  private processMappingProperties(
    props: any, olddata: any, node: any, adInfo: any, queryViewConfigurationId: string | undefined, stdGridConfigurationId: string | undefined, context: ReferenceContext
  ): void {
    ['queryViewMapping', 'detailPageMapping'].forEach((prp) => {
      if (prp === 'detailPageMapping' && node.type !== 'grid') return;
      const mappingConfigId = prp === 'queryViewMapping' ? queryViewConfigurationId : undefined;
      if (props[prp]) {
        props.queryTblmandatoryFilterSid = props[prp]?.queryTblmandatoryFilterSid;
        props[prp] = props[prp]?.queryMap;
      }
      if (this.hasChanged(props[prp], olddata?.[prp])) {
        this.handleMappingReferenceChanges(props, olddata, prp, node, adInfo, mappingConfigId, context);
      }
    });

    if (this.hasChanged(props.parameterMapping, olddata?.parameterMapping)) {
      this.createOrDelRefForMapping(olddata?.parameterMapping, props.parameterMapping, 'parameterMapping', node, 'listField',
        this.util.updateAdInfoForMapping(adInfo, 'listField', 'parameterMapping'), undefined, stdGridConfigurationId, context);
      this.createOrDelRefForMapping(olddata?.parameterMapping, props.parameterMapping, 'parameterMapping', node, 'detailField',
        this.util.updateAdInfoForMapping(adInfo, 'detailField', 'parameterMapping'), undefined, stdGridConfigurationId, context);
    }
  }

  private processPageVariableMappings(
    props: any, olddata: any, node: any, adInfo: any, queryViewConfigurationId: string | undefined, stdGridConfigurationId: string | undefined, context: ReferenceContext
  ): void {
    if (this.hasChanged(props.queryViewMappingPageVariables, olddata?.queryViewMappingPageVariables)) {
      this.createOrDelRefForMapping(olddata?.queryViewMappingPageVariables, props.queryViewMappingPageVariables,
        'queryViewMappingPageVariables', node, 'lookupField',
        this.util.updateAdInfoForMapping(adInfo, 'lookupField', 'queryViewMappingPageVariables'), undefined, queryViewConfigurationId, context);
      this.createOrDelRefForMapping(olddata?.queryViewMappingPageVariables, props.queryViewMappingPageVariables,
        'queryViewMappingPageVariables', node, 'tableField',
        this.util.updateAdInfoForMapping(adInfo, 'tableField', 'queryViewMappingPageVariables'), undefined, queryViewConfigurationId, context);
    }
    if (this.hasChanged(props.parameterMappingPageVariables, olddata?.parameterMappingPageVariables)) {
      this.createOrDelRefForMapping(olddata?.parameterMappingPageVariables, props.parameterMappingPageVariables,
        'parameterMappingPageVariables', node, 'listField',
        this.util.updateAdInfoForMapping(adInfo, 'listField', 'parameterMappingPageVariables'), undefined, stdGridConfigurationId, context);
      this.createOrDelRefForMapping(olddata?.parameterMappingPageVariables, props.parameterMappingPageVariables,
        'parameterMappingPageVariables', node, 'detailField',
        this.util.updateAdInfoForMapping(adInfo, 'detailField', 'parameterMappingPageVariables'), undefined, stdGridConfigurationId, context);
    }
  }

  private processDetailPageParameters(props: any, olddata: any, node: any, adInfo: any, context: ReferenceContext): any {
    if ((props.detailPage || olddata?.detailPage) && props.detailPage?.sid !== olddata?.detailPage?.sid) {
      props.detailPageParameters = null;
    }
    if (this.hasChanged(props.detailPageParameters, olddata?.detailPageParameters)) {
      this.createOrDelRefForMapping(olddata?.detailPageParameters, props.detailPageParameters, 'detailPageParameters', node, 'listField',
        this.util.updateAdInfoForMapping(adInfo, 'listField', 'detailPageParameters'), undefined, undefined, context);
      this.createOrDelRefForMapping(olddata?.detailPageParameters, props.detailPageParameters, 'detailPageParameters', node, 'detailField',
        this.util.updateAdInfoForMapping(adInfo, 'detailField', 'detailPageParameters'), undefined, undefined, context);
    }
    return props;
  }

  private processNavigateAndQueryListMappings(props: any, olddata: any, node: any, adInfo: any, context: ReferenceContext): void {
    if (this.hasChanged(props.navigatePageValueMapping, olddata?.navigatePageValueMapping)) {
      this.createOrDelRefForMapping(olddata?.navigatePageValueMapping, props.navigatePageValueMapping, 'navigatePageValueMapping', node, 'sourceKey',
        this.util.updateAdInfoForMapping(adInfo, 'sourceKey', 'navigatePageValueMapping'), undefined, undefined, context);
      this.createOrDelRefForMapping(olddata?.navigatePageValueMapping, props.navigatePageValueMapping, 'navigatePageValueMapping', node, 'targetKey',
        this.util.updateAdInfoForMapping(adInfo, 'targetKey', 'navigatePageValueMapping'), undefined, undefined, context);
    }
    this.processQueryListMapping(props, olddata, node, adInfo, context);
  }

  private processQueryListMapping(props: any, olddata: any, node: any, adInfo: any, context: ReferenceContext): void {
    if (!this.hasChanged(props.parameterMappingForQueryList, olddata?.parameterMappingForQueryList)) return;

    const paramMaps = props.parameterMappingForQueryList?.filter((m) => m.staticValue);
    const oldParamMaps = olddata?.parameterMappingForQueryList?.filter((m) => m.staticValue);

    if (props.parameterMappingForQueryList[0]?.staticValue) {
      this.createOrDelRefForMapping(oldParamMaps, paramMaps, 'parameterMappingForQueryList', node, 'listField',
        this.util.updateAdInfoForMapping(adInfo, 'listField', 'parameterMappingForQueryList'), undefined, undefined, context);
    } else {
      this.createOrDelRefForMapping(olddata?.parameterMappingForQueryList, props.parameterMappingForQueryList,
        'parameterMappingForQueryList', node, 'listField',
        this.util.updateAdInfoForMapping(adInfo, 'listField', 'parameterMappingForQueryList'), undefined, undefined, context);
      this.createOrDelRefForMapping(olddata?.parameterMappingForQueryList, props.parameterMappingForQueryList,
        'parameterMappingForQueryList', node, 'detailField',
        this.util.updateAdInfoForMapping(adInfo, 'detailField', 'parameterMappingForQueryList'), undefined, undefined, context);
    }
  }

  private hasChanged(newVal: any, oldVal: any): boolean {
    return (newVal || oldVal) && !isEqual(newVal, oldVal);
  }

  /**
   * Handles mapping reference changes (queryViewMapping, detailPageMapping)
   */
  private handleMappingReferenceChanges(
    props: any,
    olddata: any,
    prp: string,
    node: any,
    adInfo: any,
    mappingConfigId: string | undefined,
    context: ReferenceContext
  ): void {
    // Handle mandatory filter - create/delete reference
    let mandatoryFilterHandled = false;
    if ((props[prp] && !olddata?.[prp]) || props[prp] > olddata?.[prp]) {
      if (props.queryTblmandatoryFilterSid) {
        const filteredData = this.getNewlyAddedFlds(
          olddata?.[prp]?.map((o) => o?.lookupField?.sid),
          props[prp]?.map((o) => o?.lookupField?.sid)
        );
        if (filteredData?.length > 0) {
          filteredData.forEach((f) => {
            context.referencesTasks.addedReference.push(
              this.util.createReference(
                context.currentSelectedItem.sid,
                utilEnum.modelType.PAGE,
                f,
                utilEnum.modelType.FIELD,
                props[prp][0]?.appId,
                this.getAdditionalPath(node),
                this.getPropNameBasedOnProp(prp, 'listField'),
                false,
                this.util.updateAdInfoForMapping(adInfo, 'listField', prp),
                undefined,
                undefined,
                mappingConfigId
              )
            );
          });
          mandatoryFilterHandled = true;
        }
      }
    }
    if ((!props[prp] && olddata?.[prp]) || olddata?.[prp] > props[prp]) {
      if (olddata?.queryTblmandatoryFilterSid) {
        const filteredData = this.getNewlyAddedFlds(
          props[prp]?.map((o) => o?.lookupField.sid),
          olddata?.[prp]?.map((o) => o?.lookupField.sid)
        );
        if (filteredData?.length > 0) {
          this.createOrDelRefForMapping(
            olddata?.[prp], props[prp], prp, node, 'lookupField',
            this.util.updateAdInfoForMapping(adInfo, 'lookupField', prp),
            undefined, mappingConfigId, context
          );
          mandatoryFilterHandled = true;
        }
      }
    }

    // Create/delete reference for source field (lookupField) - only if not already handled
    if (!mandatoryFilterHandled) {
      this.createOrDelRefForMapping(
        olddata?.[prp], props[prp], prp, node, 'lookupField',
        this.util.updateAdInfoForMapping(adInfo, 'lookupField', prp),
        undefined, mappingConfigId, context
      );
    }

    // Create/delete reference for target field (tableField)
    this.createOrDelRefForMapping(
      olddata?.[prp], props[prp], prp, node, 'tableField',
      this.util.updateAdInfoForMapping(adInfo, 'tableField', prp),
      undefined, mappingConfigId, context
    );
  }
}
